import { useMemo } from 'react';
import {
  ALL_EVENT_TYPES,
  ALL_REVIEW_STATUSES,
  type EventType,
  type PendingCAFilters,
  type ReviewStatus,
} from './pendingCa.types';

interface Props {
  filters: PendingCAFilters;
  onChange: (next: PendingCAFilters) => void;
}

// 권위: ARCH_DASHBOARD §4.10 상단 필터 바.
// review_status 칩 (복수) / symbol 검색 / 기간 프리셋 / event_type 드롭다운 / confidence 슬라이더.
export function PendingCAFilterBar({ filters, onChange }: Props): JSX.Element {
  function toggleStatus(s: ReviewStatus): void {
    const next = filters.reviewStatus.includes(s)
      ? filters.reviewStatus.filter((x) => x !== s)
      : [...filters.reviewStatus, s];
    onChange({ ...filters, reviewStatus: next, page: 0 });
  }

  function applyPreset(days: number | 'today'): void {
    const now = new Date();
    let from: Date;
    if (days === 'today') {
      from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    } else {
      from = new Date(now);
      from.setDate(from.getDate() - days);
    }
    onChange({
      ...filters,
      from: from.toISOString(),
      to: now.toISOString(),
      page: 0,
    });
  }

  const activePreset = useMemo<'today' | '7d' | '30d' | 'custom' | 'all'>(() => {
    if (!filters.from && !filters.to) return 'all';
    return 'custom';
  }, [filters.from, filters.to]);

  return (
    <div className="pca-filter">
      <div className="pca-filter__group" style={{ flex: 1, minWidth: 220 }}>
        <label className="pca-filter__label">review_status</label>
        <div className="pca-chip-row">
          {ALL_REVIEW_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className={
                'pca-chip' +
                (filters.reviewStatus.includes(s) ? ' pca-chip--active' : '')
              }
              onClick={() => toggleStatus(s)}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="pca-filter__group">
        <label className="pca-filter__label" htmlFor="pca-symbol">
          종목/법인
        </label>
        <input
          id="pca-symbol"
          className="pca-input"
          type="text"
          placeholder="005930 또는 삼성전자"
          value={filters.symbol}
          onChange={(e) => onChange({ ...filters, symbol: e.target.value, page: 0 })}
          style={{ minWidth: 180 }}
        />
      </div>

      <div className="pca-filter__group">
        <label className="pca-filter__label">기간</label>
        <div className="pca-chip-row">
          <button
            type="button"
            className={'pca-chip' + (activePreset === 'today' ? ' pca-chip--active' : '')}
            onClick={() => applyPreset('today')}
          >
            오늘
          </button>
          <button
            type="button"
            className="pca-chip"
            onClick={() => applyPreset(7)}
          >
            7d
          </button>
          <button
            type="button"
            className="pca-chip"
            onClick={() => applyPreset(30)}
          >
            30d
          </button>
          <button
            type="button"
            className={'pca-chip' + (activePreset === 'all' ? ' pca-chip--active' : '')}
            onClick={() => onChange({ ...filters, from: '', to: '', page: 0 })}
          >
            전체
          </button>
        </div>
      </div>

      <div className="pca-filter__group">
        <label className="pca-filter__label" htmlFor="pca-etype">
          event_type
        </label>
        <select
          id="pca-etype"
          className="pca-select"
          value={filters.eventType}
          onChange={(e) =>
            onChange({
              ...filters,
              eventType: e.target.value as EventType | '',
              page: 0,
            })
          }
        >
          <option value="">전체</option>
          {ALL_EVENT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div className="pca-filter__group">
        <label className="pca-filter__label" htmlFor="pca-conf">
          confidence ≥ {filters.confidenceMin.toFixed(2)}
        </label>
        <input
          id="pca-conf"
          className="pca-slider"
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={filters.confidenceMin}
          onChange={(e) =>
            onChange({
              ...filters,
              confidenceMin: Number(e.target.value),
              page: 0,
            })
          }
        />
      </div>
    </div>
  );
}
